import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AuthShell, FeatIcon, SvgArrow,
} from "./AuthShell.js";
import { useAuth } from "../../lib/auth-context.js";

/**
 * /verify — 6-digit OTP entry, exchanged for a session via POST /api/auth/verify.
 *
 * The code is sent to the user's email (Resend). The boxes start empty —
 * the user types or pastes the code from their inbox.
 */
export const Verify = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isNew = params.get("new") === "1";
  const isReset = params.get("reset") === "1";
  const { verifyCode, requestSignIn, requestPasswordReset } = useAuth();
  const email = sessionStorage.getItem("wo.auth.email") || "";
  const rememberMe = sessionStorage.getItem("wo.auth.rememberMe") === "1";

  const [digits, setDigits] = useState<string[]>(() => Array(6).fill(""));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [secs, setSecs] = useState(42);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If we don't even know the email, send the user back to start.
    if (!email) { navigate("/sign-in", { replace: true }); return; }
    const firstEmpty = digits.findIndex((d) => !d);
    inputs.current[firstEmpty === -1 ? 5 : firstEmpty]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (secs <= 0) return;
    const t = setTimeout(() => setSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);

  const setDigit = (i: number, v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, 1);
    setDigits((prev) => { const next = [...prev]; next[i] = clean; return next; });
    if (clean && i < 5) inputs.current[i + 1]?.focus();
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const onVerify = async () => {
    const code = digits.join("");
    if (code.length !== 6) return setError("Enter all six digits.");
    setSubmitting(true); setError(null);
    const err = await verifyCode(email, code, rememberMe);
    setSubmitting(false);
    if (err) return setError(err);
    sessionStorage.removeItem("wo.auth.devOtp");
    if (isReset) navigate("/reset?reset=1", { replace: true });
    else if (isNew) navigate("/reset?new=1", { replace: true });
    else navigate("/welcome", { replace: true });
  };

  const onResend = async () => {
    if (secs > 0 || !email) return;
    const result = isReset
      ? await requestPasswordReset(email)
      : await requestSignIn(email);
    if (result.ok) {
      setDigits(Array(6).fill(""));
      setSecs(42); setError(null);
    } else {
      setError(result.message);
    }
  };

  return (
    <AuthShell
      brand={{
        eyebrow: "A small key",
        quote: <>Six digits.<br /><em>One quiet step</em> closer to your private space.</>,
        cite: "— On verification",
        features: [
          { icon: FeatIcon.clock, label: "Expires in 10 minutes" },
          { icon: FeatIcon.lock, label: "End-to-end encrypted" },
        ],
      }}
      topRight={<Link to="/sign-in" className="btn-ghost">← Back</Link>}
      footer={<>Trouble signing in? <Link to="/forgot">Get help →</Link></>}
    >
      <div className="stepper">
        <div className="stepper-dot on" />
        <div className="stepper-dot on" />
        {isNew && <div className="stepper-dot" />}
      </div>

      <div className="eyebrow">Step 2 of {isNew ? 3 : 2}</div>
      <h1 className="wo-title" style={{ marginTop: 6 }}>Check your email.</h1>
      <p className="wo-sub">
        We sent a 6-digit code to <strong>{email}</strong>. It expires in 10 minutes.
      </p>

      <div className="field">
        <label className="field-label">
          <span>Code</span>
          <span className="hint">Tip — paste it from your email</span>
        </label>
        <div className="otp-grid">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { inputs.current[i] = el; }}
              className={`otp-box${d ? " filled" : ""}`}
              maxLength={1}
              inputMode="numeric"
              pattern="[0-9]*"
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
            />
          ))}
        </div>
        <div className="field-help" style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
          <span>Didn&apos;t get it? Check your spam folder.</span>
          <button
            type="button"
            onClick={onResend}
            style={{
              background: "transparent", border: "none", padding: 0, cursor: secs === 0 ? "pointer" : "default",
              color: secs === 0 ? "rgb(var(--wo-teal))" : "rgb(var(--wo-muted))",
              fontWeight: 600, fontFamily: "inherit", fontSize: "inherit",
            }}
          >
            {secs > 0 ? `Resend in 0:${String(secs).padStart(2, "0")}` : "Resend code"}
          </button>
        </div>
      </div>

      {error && (
        <div className="field-help" style={{ color: "rgb(var(--wo-danger))", marginTop: 8 }}>{error}</div>
      )}

      <button type="button" className="btn-primary" onClick={onVerify} style={{ marginTop: 18 }} disabled={submitting}>
        {submitting ? "Verifying…" : "Verify code"}
        {SvgArrow}
      </button>

      <button
        type="button"
        className="btn-ghost"
        style={{ width: "100%", marginTop: 10, height: 44 }}
        onClick={() => navigate(-1)}
      >
        Use a different email
      </button>

      <p className="reflect">&ldquo;Patience is a small form of kindness.&rdquo;</p>
    </AuthShell>
  );
};
